import React, { useState, useEffect, useRef } from 'react';
import { NotificationGroup, NotificationColor } from '@/shared/types/message';
import { notificationService } from '../../services/NotificationService';
import { Alert } from '@/modules/Alert';
import { Icon } from '@/modules/Icon';
import { AlertColor } from '@/shared/types/alert';
import { PaperclipOutlined } from '@/shared/modules/Icons/componentes/outline';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';

interface NotificationItemProps {
  group: NotificationGroup;
}

export const NotificationItem = React.memo(({ group }: NotificationItemProps) => {
  const { notification, count, isLeaving, color } = group;
  const [isVisible, setIsVisible] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Manejar la visibilidad basada en isLeaving
  useEffect(() => {
    if (isLeaving) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 300);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLeaving]);

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    notificationService.dismissGroup(color);
    notification.onClose?.();
  };

  if (!isVisible) {
    return null;
  }

  // Mapear NotificationColor a AlertColor
  const alertColorMap: Record<NotificationColor, AlertColor> = {
    brand: 'brand',
    neutral: 'neutral',
  };

  const alertColor = alertColorMap[color];

  return (
    <Alert
      color={alertColor}
      variant={notification.variant || 'outlined'}
      message={notification.message}
      title={notification.title}
      description={notification.description}
      showCloseButton={notification.showCloseButton}
      open={true}
      onClose={handleClose}
      onPrimaryClick={notification.onPrimaryClick}
      onSecondaryClick={notification.onSecondaryClick}
      primaryButtonText={notification.primaryButtonText}
      secondaryButtonText={notification.secondaryButtonText}
      isLeaving={isLeaving}
      onClick={notification.onClick}
      animation={notification.animation || 'fade'}
      size={notification.size || 'sm'}
      duration={notification.duration}
      avatar={notification.avatar}
    >
      {/* Contador de mensajes (solo si > 1) */}
      {count > 1 && (
        <div className="ui-mb-2 ui-flex ui-items-center ui-justify-center ui-gap-1 ui-py-2">
          <Icon name={faPaperPlane} size="xs" color="tertiary" />
          <span className="ui-text-xs ui-text-tertiary">Tienes {count} mensajes nuevos</span>
        </div>
      )}
    </Alert>
  );
});

NotificationItem.displayName = 'NotificationItem';
